// components/FullGalleryWrapper.jsx

import React, { useState, useCallback, lazy, Suspense } from 'react';
import AllGallery from '../pages/AllGallery'; // The component you provided
// NOTE: Adjust the path if ZoomImageModal is not in '../widgets/'
const ZoomImageModal = lazy(() => import('./ZoomImageModal')); 

// Configuration constant for the zoom logic (must be consistent with the modal logic)
const ZOOM_SCALE = 8; 

const FullGalleryWrapper = () => {
    // 1. Modal States
    const [selectedImage, setSelectedImage] = useState(null);
    const [zoomPreviewPosition, setZoomPreviewPosition] = useState({ x: 0, y: 0 });
    const [showZoomPreview, setShowZoomPreview] = useState(false);

    // 2. Function to open the modal (passed down to AllGallery)
    const openImageModal = useCallback((item) => {
        setSelectedImage(item);
        document.body.style.overflow = 'hidden'; 
    }, []);

    // 3. Function to close the modal
    const closeImageModal = useCallback(() => {
        setSelectedImage(null);
        setShowZoomPreview(false);
        document.body.style.overflow = '';
    }, []);
    
    // 4. Zoom Logic (Handles the cursor position and zoom preview visibility)
    const handleMouseMove = useCallback((e) => {
        if (!selectedImage) return;
        const container = e.currentTarget;
        const img = container.querySelector('img');
        if (!img) return;

        const rect = img.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const boundedX = Math.max(0, Math.min(x, rect.width));
        const boundedY = Math.max(0, Math.min(y, rect.height));

        const xPct = boundedX / rect.width;
        const yPct = boundedY / rect.height;

        const adjustedX = (xPct * 100 - (50 / ZOOM_SCALE));
        const adjustedY = (yPct * 100 - (50 / ZOOM_SCALE));

        setZoomPreviewPosition({
            x: Math.max(0, Math.min(100, adjustedX * (100 / (100 - (100 / ZOOM_SCALE))))),
            y: Math.max(0, Math.min(100, adjustedY * (100 / (100 - (100 / ZOOM_SCALE)))))
        });
        setShowZoomPreview(true);
    }, [selectedImage]);

    // Fallback for lazy loading (includes blur/dim style)
    const SuspenseFallback = <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/10 backdrop-blur-sm text-white">Loading Image Viewer...</div>;

    return (
        <>
            {/* Render the main gallery view, passing the modal opener */}
            <AllGallery openImageModal={openImageModal} />

            {/* Conditionally render the lazy-loaded Zoom Modal */}
            {selectedImage && (
                <Suspense fallback={SuspenseFallback}>
                    <ZoomImageModal 
                        selectedImage={selectedImage}
                        closeImageModal={closeImageModal}
                        handleMouseMove={handleMouseMove}
                        zoomPreviewPosition={zoomPreviewPosition}
                        showZoomPreview={showZoomPreview}
                        setShowZoomPreview={setShowZoomPreview}
                    />
                </Suspense>
            )}
        </>
    );
};

export default FullGalleryWrapper;